import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { fontMono, ink, semantic, space, typeScale, weight } from '@repo/design-tokens';
import { Button, Icon, Input, Note } from '@repo/design-system';
import type { IconName, TableColumn } from '@repo/design-system';
import { CheckCard } from '@repo/ui';
import type { CheckCardTone } from '@repo/ui';
import {
  VALUE_PROBLEM_KINDS,
  columnLabel,
  describeFixedKind,
  editKey,
} from '@repo/front-end-utils';
import type {
  AssetValueCheck,
  AssetValueEdits,
  LeftOutLines,
  RepeatedNameLine,
  ValueKindGroup,
  ValueProblemKind,
  ValueProblemLine,
} from '@repo/front-end-utils';

/**
 * **Upload 2: fix the values** (`equipment-page-upload-values`, frame 03 of Rob's 12 October
 * *Equipment Page* design): one `CheckCard` per kind of problem, with what was found and the right
 * form, the fixes a person can make, and the repeated names said once so nothing is a surprise.
 *
 * A page composition of design-system parts — `CheckCard` (`@repo/ui`), `Table` (through the
 * card's `lines`), compact `Input`, `Button`, `Note`, `Icon`. It holds no file state:
 * `AssetCsvUploader` owns the edits and the left-out lines, re-runs the checks and passes the
 * answer in; every press here is a callback.
 *
 * What it does own is what the cards look like over time: the kinds seen since entering Values
 * (a kind that empties turns `fixed` in its place, never vanishes — R11), which tables are open for
 * fixing, and where focus goes after a press (R17).
 */

/** Every line each kind has held since entering Values, keyed `line:column`, first sighting kept. */
export type SeenValueLines = Map<ValueProblemKind, Map<string, ValueProblemLine>>;

export interface EquipmentUploadValuesProps {
  /** The checks over the current edits, the server's refusals for this file merged in (R10). */
  check: AssetValueCheck;
  edits: AssetValueEdits;
  leftOut: LeftOutLines;
  /** *Convert All* (R7): write every line's certain fix. */
  onConvertAll: (group: ValueKindGroup) => void;
  /** One cell's value from *Fix in Place* (R8), committed on Enter or blur. */
  onEdit: (line: number, column: string, value: string) => void;
  /**
   * *Leave Out*, a whole kind's lines or one line (R8), with the kind of the card it was pressed
   * on — a line left out from a fixed card's table is in no kind any more, and must still name one.
   */
  onLeaveOut: (lines: readonly number[], kind: ValueProblemKind) => void;
  /**
   * Where the lines seen are kept: the uploader's, so a visit to Columns does not lose the fixed
   * cards while their edits stand. Without one, the panel keeps its own.
   */
  seen?: React.MutableRefObject<SeenValueLines>;
  /** Every action off — set while the upload runs. */
  disabled?: boolean;
  /** Where focus goes when no card is left open (R17): the footer's *Upload {rows} Assets*. */
  uploadRef: React.RefObject<HTMLButtonElement | null>;
  /**
   * An adoption file's rows the upload would write with no BID (`equipment-page-adoption`, R5), as
   * the server's check counts them — rows updating a register row that has a BID keep it, which
   * the browser cannot see. `null` until that check has answered for this file: the card waits
   * rather than show a count the server would lower.
   */
  rowsWithoutBid?: number | null;
}

const plural = (n: number, one: string, many: string): string =>
  `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/** What a kind's lines are, for the action names (criterion 17): *Convert all 12 dates*. */
const NOUNS: Readonly<Record<ValueProblemKind, [string, string]>> = {
  'date-day-first': ['date', 'dates'],
  'date-month-first': ['date', 'dates'],
  'date-order-unknown': ['date', 'dates'],
  'date-unreadable': ['date', 'dates'],
  'money-sign': ['cost', 'costs'],
  'money-unreadable': ['cost', 'costs'],
  'currency-code': ['currency', 'currencies'],
  'name-missing': ['row with no name', 'rows with no name'],
  'identifier-repeated': ['repeated Asset Identifier', 'repeated Asset Identifiers'],
  'adopted-bid': ['BID', 'BIDs'],
  'adopted-reference-id': ['reference id', 'reference ids'],
  'adopted-capacity': ['capacity', 'capacities'],
  'adopted-model-3d': ['3D model', '3D models'],
  'server-refused': ['refused row', 'refused rows'],
};

/** The rows a kind's *Leave Out* drops: *Leave out 2 rows with no name*. */
const ROW_NOUNS: Readonly<Record<ValueProblemKind, [string, string]>> = {
  'date-day-first': ['row with a day-first date', 'rows with day-first dates'],
  'date-month-first': ['row with a month-first date', 'rows with month-first dates'],
  'date-order-unknown': [
    'row with a date that reads two ways',
    'rows with dates that read two ways',
  ],
  'date-unreadable': ['row with an unreadable date', 'rows with unreadable dates'],
  'money-sign': ['row with a currency sign', 'rows with a currency sign'],
  'money-unreadable': ['row with an unreadable cost', 'rows with unreadable costs'],
  'currency-code': ['row with an unreadable currency', 'rows with unreadable currencies'],
  'name-missing': ['row with no name', 'rows with no name'],
  'identifier-repeated': [
    'row with a repeated Asset Identifier',
    'rows with repeated Asset Identifiers',
  ],
  'adopted-bid': ['row with a BID not for this twin', 'rows with BIDs not for this twin'],
  'adopted-reference-id': [
    'row with an unreadable reference id',
    'rows with unreadable reference ids',
  ],
  'adopted-capacity': ['row with an unusable capacity', 'rows with unusable capacities'],
  'adopted-model-3d': ['row with an unusable 3D model', 'rows with unusable 3D models'],
  'server-refused': ['row the server would refuse', 'rows the server would refuse'],
};

/** R11's sticker glyphs. */
const KIND_ICONS: Readonly<Record<ValueProblemKind, IconName>> = {
  'date-day-first': 'calendar',
  'date-month-first': 'calendar',
  'date-order-unknown': 'calendar',
  'date-unreadable': 'calendar',
  'money-sign': 'sterling-sign',
  'money-unreadable': 'sterling-sign',
  'currency-code': 'sterling-sign',
  'name-missing': 'tag',
  'identifier-repeated': 'barcode',
  'adopted-bid': 'barcode',
  'adopted-reference-id': 'tag',
  'adopted-capacity': 'hashtag',
  'adopted-model-3d': 'cube',
  'server-refused': 'circle-exclamation',
};

/** Lines, counted once each: a row refused on two cells is one row. */
const rowCount = (lines: readonly ValueProblemLine[]): number =>
  new Set(lines.map(line => line.line)).size;

/** A kind's whole fix is certain: *Convert All* is its one action, as frame 03 draws it. */
const allCertain = (group: ValueKindGroup): boolean => group.lines.every(line => line.fix);

/**
 * Values to fix, every error kind together (R12): one per cell, and one per row for a server
 * refusal, which names a row rather than a cell.
 */
export const problemsLeft = (check: AssetValueCheck): number =>
  check.kinds.reduce(
    (sum, group) =>
      sum + (group.kind === 'server-refused' ? rowCount(group.lines) : group.lines.length),
    0,
  );

/** R18, on entering Values. */
export const valuesEnteredSentence = (check: AssetValueCheck): string =>
  check.kinds.length === 0
    ? 'No problems found. Checking with the server.'
    : `${plural(check.kinds.length, 'kind', 'kinds')} of problem, ` +
      `${plural(problemsLeft(check), 'value', 'values')} to fix.`;

/** R18, after *Convert All*: *12 dates converted to 2025-04-03 form.* */
export const convertedSentence = (group: ValueKindGroup): string => {
  const n = group.lines.filter(line => line.fix).length;
  const [one, many] = NOUNS[group.kind];
  if (group.kind === 'money-sign') return `${plural(n, one, many)} converted to plain numbers.`;
  if (group.kind === 'currency-code') return `${plural(n, one, many)} converted to 3-letter codes.`;
  return `${plural(n, one, many)} converted to 2025-04-03 form.`;
};

/**
 * The gate (R10, R12): Upload is offered only when no problem is left on the page — the browser's
 * kinds and the server's refusals together — and the server's latest answer, for exactly the file
 * built from these edits, refuses nothing.
 */
export const uploadOffered = (
  check: AssetValueCheck | null,
  answer: { rejected: readonly unknown[] } | null,
): boolean =>
  check !== null && check.kinds.length === 0 && answer !== null && answer.rejected.length === 0;

/** The footer's note on Values (R12). */
export const valuesFootNote = (check: AssetValueCheck, checking: boolean, ready: boolean) => {
  const rows = plural(check.rows, 'row', 'rows');
  if (checking) return 'Checking the file…';
  if (ready) return `Ready to upload · ${rows}`;
  const p = problemsLeft(check);
  return p > 0 ? `${plural(p, 'problem', 'problems')} left · ${rows}` : rows;
};

/** One line of a kind's table. */
type LineRow = ValueProblemLine & { key: string };

/** *Will be*: the cell as it will be sent, editable in place (R8). */
const WillBeInput: React.FC<{
  value: string;
  label: string;
  error: string | null;
  disabled: boolean;
  onCommit: (value: string) => void;
}> = ({ value, label, error, disabled, onCommit }) => {
  const [draft, setDraft] = useState(value);
  // A value changed from outside — Convert All, another commit — replaces an untouched draft.
  useEffect(() => setDraft(value), [value]);
  const errorId = useId();
  const commit = () => {
    if (draft !== value) onCommit(draft);
  };
  return (
    <>
      <Input
        size="compact"
        value={draft}
        aria-label={label}
        invalid={error !== null}
        aria-describedby={error !== null ? errorId : undefined}
        disabled={disabled}
        onChange={event => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            // Restores, and the blur that follows commits nothing.
            event.preventDefault();
            setDraft(value);
          }
        }}
      />
      {/* The table's rows are one fixed height and clip, so the sentence is read from here and
          shown under the card instead (`uv-errors`). */}
      {error !== null ? (
        <span className="visually-hidden" id={errorId}>
          {error}
        </span>
      ) : null}
    </>
  );
};

const EquipmentUploadValues: React.FC<EquipmentUploadValuesProps> = ({
  check,
  edits,
  leftOut,
  onConvertAll,
  onEdit,
  onLeaveOut,
  disabled = false,
  uploadRef,
  seen: seenStore,
  rowsWithoutBid = null,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  /*
   * Every line each kind has held since entering Values, first sighting kept: a kind that empties
   * stays as a `fixed` card in its place (R11), and its summary needs what the lines were. Grown
   * as the checks re-run, never shrunk; the uploader starts it afresh when it forgets the edits.
   */
  const ownSeen = useRef<SeenValueLines>(new Map());
  const seenRef = seenStore ?? ownSeen;
  const seen = useMemo(() => {
    for (const group of check.kinds) {
      let lines = seenRef.current.get(group.kind);
      if (!lines) {
        lines = new Map();
        seenRef.current.set(group.kind, lines);
      }
      for (const line of group.lines) {
        const key = `${line.line}:${line.column ?? ''}`;
        if (!lines.has(key)) lines.set(key, line);
      }
    }
    return VALUE_PROBLEM_KINDS.filter(kind => seenRef.current.has(kind)).map(kind => ({
      kind,
      lines: Array.from(seenRef.current.get(kind)!.values()).sort((a, b) => a.line - b.line),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on `check` only: `seenRef` is a ref the memo grows, not an input
  }, [check]);

  const groupOf = useMemo(
    () => new Map(check.kinds.map(group => [group.kind, group] as const)),
    [check],
  );
  /** The open card: the first error card with lines left, else none (R11). */
  const openKind = check.kinds[0]?.kind ?? null;
  /** The first certain-fix card's *Convert All* is primary; later ones frosted (R7). */
  const firstCertain = check.kinds.find(group => group.lines.some(line => line.fix))?.kind ?? null;

  /** Kinds whose table is open for fixing: *Fix in Place*, or *Edit* on a fixed card. */
  const [fixing, setFixing] = useState<ReadonlySet<ValueProblemKind>>(new Set());
  const [seeNames, setSeeNames] = useState(false);

  /*
   * ── FOCUS (R17) ────────────────────────────────────────────────────────────────────────────
   * Asked for by a press and done after the commit that re-runs the checks, so it lands on the
   * card list as it now stands.
   */
  const focusOwed = useRef<
    | { to: 'next' }
    | { to: 'input'; kind: ValueProblemKind }
    | { to: 'next-if-fixed'; kind: ValueProblemKind }
    | null
  >(null);
  const focusNext = () => {
    const root = rootRef.current;
    const action = root?.querySelector<HTMLElement>(
      '.tl-check-card[data-open] .tl-cc-actions button:not(:disabled)',
    );
    if (action) action.focus();
    else if (uploadRef.current && !uploadRef.current.disabled) uploadRef.current.focus();
    else root?.focus();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every commit: owed focus lands once the checks have re-run
  useEffect(() => {
    const owed = focusOwed.current;
    if (!owed) return;
    focusOwed.current = null;
    if (owed.to === 'next') {
      focusNext();
    } else if (owed.to === 'input') {
      const input = rootRef.current?.querySelector<HTMLElement>(
        `.tl-check-card[data-kind="${owed.kind}"] input`,
      );
      // A table with nothing left to type in: focus moves on rather than to <body>.
      if (input) input.focus();
      else focusNext();
    } else if (!groupOf.has(owed.kind)) {
      // The kind's last line was just fixed: the card turns fixed, its table closes, and focus
      // moves on as after *Convert All*.
      setFixing(current => {
        const next = new Set(current);
        next.delete(owed.kind);
        return next;
      });
      focusNext();
    }
  });

  /*
   * The server refusing rows closes Upload, which may hold focus while it checks (R17): focus
   * goes to the refused card's first action rather than being dropped on <body>.
   */
  const refusedShown = groupOf.has('server-refused');
  const refusedBefore = useRef(refusedShown);
  useEffect(() => {
    const appeared = refusedShown && !refusedBefore.current;
    refusedBefore.current = refusedShown;
    if (!appeared) return;
    const active = document.activeElement;
    if (
      active === null ||
      active === document.body ||
      active === uploadRef.current ||
      !active.isConnected
    ) {
      focusNext();
    }
  });

  const convertAll = (group: ValueKindGroup) => {
    focusOwed.current = { to: 'next' };
    onConvertAll(group);
  };
  const leaveOut = (lines: readonly number[], kind: ValueProblemKind) => {
    focusOwed.current = { to: 'next' };
    onLeaveOut(lines, kind);
  };
  const openTable = (kind: ValueProblemKind) => {
    focusOwed.current = { to: 'input', kind };
    setFixing(current => new Set(current).add(kind));
  };
  const commit = (kind: ValueProblemKind, line: number, column: string, value: string) => {
    const group = groupOf.get(kind);
    if (group && group.lines.every(each => each.line === line && each.column === column)) {
      focusOwed.current = { to: 'next-if-fixed', kind };
    }
    onEdit(line, column, value);
  };

  const valueNow = (line: ValueProblemLine): string =>
    line.column === null ? '' : (edits.get(editKey(line.line, line.column)) ?? line.found);

  /** The line table's columns (frame 03): *Line*, *Asset*, *Found*, *Will be*. */
  const lineColumns = (kind: ValueProblemKind, editing: boolean): TableColumn<LineRow>[] => {
    const reasons = new Map(
      (groupOf.get(kind)?.lines ?? []).map(line => [`${line.line}:${line.column}`, line.reason]),
    );
    const errorOf = (row: LineRow): string | null =>
      reasons.get(`${row.line}:${row.column}`) ?? null;
    const columns: TableColumn<LineRow>[] = [
      { key: 'line', header: 'Line', width: '64px', render: row => row.line, readsIndex: false },
      {
        key: 'asset',
        header: 'Asset',
        render: row => row.name ?? <span className="uv-no-name">no name</span>,
        readsIndex: false,
      },
      {
        key: 'found',
        header: 'Found',
        // A value that stops the upload and can be fixed: a problem with a fix
        // (`onboarding-status-colours-equipment`, R9).
        tone: 'pink',
        render: row => <span className="uv-value">{row.found}</span>,
        readsIndex: false,
      },
      {
        key: 'will-be',
        header: 'Will be',
        tone: 'mint',
        readsIndex: false,
        render: row => {
          if (leftOut.has(row.line)) return <span className="uv-left-out">Left out</span>;
          if (!editing || row.column === null) {
            const fixed = row.fix && row.column !== null ? row.fix[row.column] : undefined;
            return <span className="uv-value">{fixed ?? ''}</span>;
          }
          const column = row.column;
          return (
            <WillBeInput
              value={valueNow(row)}
              label={`${columnLabel(column)} on line ${row.line}`}
              error={errorOf(row)}
              disabled={disabled}
              onCommit={value => commit(kind, row.line, column, value)}
            />
          );
        },
      },
    ];
    if (editing) {
      columns.push({
        key: 'leave-out',
        header: <span className="visually-hidden">Leave out</span>,
        width: '112px',
        readsIndex: false,
        render: row =>
          leftOut.has(row.line) ? null : (
            <Button
              type="button"
              variant="text"
              size="xs"
              disabled={disabled}
              aria-label={`Leave out line ${row.line}`}
              onClick={() => leaveOut([row.line], kind)}
            >
              Leave Out
            </Button>
          ),
      });
    }
    return columns;
  };

  const card = ({ kind, lines }: { kind: ValueProblemKind; lines: ValueProblemLine[] }) => {
    const group = groupOf.get(kind);
    const editing = fixing.has(kind);
    const [one, many] = NOUNS[kind];
    const open = kind === openKind;
    let tone: CheckCardTone;
    let title: string;
    let where: string;
    let actions: React.ReactNode;

    if (group) {
      tone = 'error';
      title = group.title;
      where = group.where;
      const certain = group.lines.filter(line => line.fix).length;
      const editable = group.lines.some(line => line.column !== null);
      const rows = rowCount(group.lines);
      actions = (
        <>
          {certain > 0 && (
            <Button
              type="button"
              variant={kind === firstCertain ? 'primary' : 'frosted'}
              size="sm"
              disabled={disabled}
              aria-label={`Convert all ${plural(certain, one, many)}`}
              iconLeft={<Icon name="wand-magic-sparkles" variant="solid" size="xs" />}
              onClick={() => convertAll(group)}
            >
              Convert All {certain.toLocaleString('en-GB')}
            </Button>
          )}
          {!allCertain(group) && editable && !editing && (
            <Button
              type="button"
              variant="frosted"
              size="sm"
              disabled={disabled}
              aria-label={`Fix ${plural(group.lines.length, one, many)} in place`}
              iconLeft={<Icon name="pen" variant="solid" size="xs" />}
              onClick={() => openTable(kind)}
            >
              Fix in Place
            </Button>
          )}
          {!allCertain(group) && (
            <Button
              type="button"
              variant="text"
              size="sm"
              disabled={disabled}
              aria-label={`Leave out ${plural(rows, ...ROW_NOUNS[kind])}`}
              iconLeft={<Icon name="eye-slash" variant="solid" size="xs" />}
              onClick={() =>
                leaveOut(Array.from(new Set(group.lines.map(line => line.line))), kind)
              }
            >
              Leave Out
            </Button>
          )}
        </>
      );
    } else {
      tone = 'fixed';
      const summary = describeFixedKind(kind, lines, edits, leftOut);
      // Nothing on the page changed it, so there is no fix to show (its lines are elsewhere).
      if (summary === null) return null;
      ({ title, where } = summary);
      // *Edit* reopens the table at its first input (R17), so a card with none (every line left
      // out, or refusals with no column to type in) offers nothing.
      const editable = lines.some(line => line.column !== null && !leftOut.has(line.line));
      actions =
        editing || !editable ? null : (
          <Button
            type="button"
            variant="text"
            size="sm"
            disabled={disabled}
            aria-label={`Edit fixed ${many}`}
            iconLeft={<Icon name="pen" variant="solid" size="xs" />}
            onClick={() => openTable(kind)}
          >
            Edit
          </Button>
        );
    }

    // The open card shows its first three lines (frame 03); a table open for fixing shows every
    // line seen, fixed ones too, so a fix never moves the row being worked on.
    const tableRows = editing ? lines : open && group ? group.lines.slice(0, 3) : [];
    // A value typed in place that the rule still refuses: its sentence, under the card, since the
    // input's row cannot grow to hold it. Lines not yet edited show their fault in *Found*.
    const stillWrong =
      editing && group
        ? group.lines.filter(
            line => line.column !== null && edits.has(editKey(line.line, line.column)),
          )
        : [];
    return (
      <React.Fragment key={kind}>
        <CheckCard<LineRow>
          data-kind={kind}
          tone={tone}
          icon={
            <Icon name={tone === 'fixed' ? 'check' : KIND_ICONS[kind]} variant="solid" size="sm" />
          }
          title={title}
          where={where}
          found={group && group.use !== undefined ? group.found : undefined}
          use={group?.use}
          actions={actions}
          open={open}
          lines={{
            columns: lineColumns(kind, editing),
            data: tableRows.map(line => ({ ...line, key: `${line.line}:${line.column ?? ''}` })),
            keyExtractor: row => row.key,
            label: `Lines with ${title}`,
          }}
        />
        {stillWrong.length > 0 && (
          // Hidden from assistive tech: each input already carries its own sentence.
          <ul className="uv-errors" aria-hidden="true">
            {stillWrong.map(line => (
              <li key={`${line.line}:${line.column}`}>
                Line {line.line}: {line.reason}
              </li>
            ))}
          </ul>
        )}
      </React.Fragment>
    );
  };

  const names = check.repeatedNames;
  const nameColumns: TableColumn<RepeatedNameLine>[] = [
    { key: 'line', header: 'Line', width: '64px', render: row => row.line, readsIndex: false },
    { key: 'as-written', header: 'As written', render: row => row.asWritten, readsIndex: false },
    {
      key: 'will-be',
      header: 'Will be',
      tone: 'mint',
      render: row => <span className="uv-value">{row.willBe}</span>,
      readsIndex: false,
    },
  ];

  return (
    <Root ref={rootRef} tabIndex={-1} data-testid="upload-values">
      {seen.length === 0 && (
        <Note
          tone={semantic.status.roles.neutral.noteTone}
          icon={<Icon name="circle-info" size="xs" />}
        >
          No problems found
        </Note>
      )}
      {seen.map(card)}
      {/* An adoption file's rows with no BID (`equipment-page-adoption`, R5): information, never a
          problem, as the FR9 banner it replaces was. */}
      {rowsWithoutBid !== null && rowsWithoutBid > 0 && (
        <CheckCard
          tone="info"
          data-kind="rows-without-bid"
          icon={<Icon name="barcode" variant="solid" size="sm" />}
          title={
            rowsWithoutBid === 1
              ? '1 row has no BID: it gets one when placed'
              : `${rowsWithoutBid.toLocaleString('en-GB')} rows have no BID: they get one when placed`
          }
          where="Nothing to do"
        />
      )}
      {names && (
        <CheckCard<RepeatedNameLine>
          tone="info"
          icon={<Icon name="hashtag" variant="solid" size="sm" />}
          title={names.title}
          where={names.where}
          actions={
            <Button
              type="button"
              variant="text"
              size="sm"
              aria-label={
                seeNames
                  ? `Hide the ${plural(names.rows, 'repeated name', 'repeated names')}`
                  : `See the ${plural(names.rows, 'repeated name', 'repeated names')}`
              }
              aria-expanded={seeNames}
              iconLeft={<Icon name={seeNames ? 'eye-slash' : 'eye'} variant="solid" size="xs" />}
              onClick={() => setSeeNames(shown => !shown)}
            >
              {seeNames ? 'Hide Them' : 'See Them'}
            </Button>
          }
          lines={{
            columns: nameColumns,
            data: seeNames ? names.lines : [],
            keyExtractor: row => row.line,
            label: `Lines with ${names.title}`,
          }}
        />
      )}
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};
  outline: none;

  /* Found and Will be in the frame's mono, as the card's own pills are. */
  .uv-value {
    font-family: ${fontMono};
    font-weight: ${weight.semibold};
    font-size: ${typeScale.caption1.fontSize};
  }
  .uv-no-name,
  .uv-left-out {
    color: ${ink[4]};
  }
  .uv-errors {
    list-style: none;
    margin: 0;
    padding: 0 ${space[4]};
    font-size: ${typeScale.caption2.fontSize};
    line-height: ${typeScale.caption2.lineHeight};
    color: ${semantic.status.roles.problem.ink};
  }
`;

export default EquipmentUploadValues;
